/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React from 'react';
import { css } from 'aphrodite';
import { fonts } from '@blend/consumer-components';
import { GenerateStyles, useConsumerTheme } from '../../../hooks/useConsumerTheme';

import { DocSubheaderWrapper } from '../../common/DocSubheaderWrapper/DocSubheaderWrapper';
import { DocumentsAPI } from '../../../api/common/documents';
import SigningDocument from '../../common/Documents/SigningDocument';
import { SigningActionType } from '@shared/constants/signing';
import { DocumentStatus } from '@shared/constants/document';

export namespace DocSubheader {
  export interface Props extends React.HTMLAttributes<HTMLDivElement> {
    selectedDocument: SigningDocument;
    signingAction: DocumentsAPI.ActionResponse;
  }
}

const generateStyles: GenerateStyles = ({ colors }) => ({
  wrapper: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  secondaryDescription: {
    fontSize: 12,
    color: colors.darkerGray,
  },
  primaryDescription: {
    fontWeight: fonts.weight.bold,
  },
  info: {
    color: colors.reflex,
    background: colors.reflexLight08,
  },
  affirm: {
    color: colors.catalyst,
    background: colors.catalystLight03,
  },
  void: {
    color: colors.pinkDark33,
    background: colors.pinkLight08,
  },
});

export const DocSubheader: React.SFC<DocSubheader.Props> = ({ selectedDocument, signingAction }) => {
  const { styles } = useConsumerTheme(generateStyles);
  const subheaderAttributes = (() => {
    if (signingAction.type === SigningActionType.SIGN) {
      return {
        primaryDescription: `${signingAction.signerName} is signing this document`,
        secondaryDescription: 'Once everyone has signed, this document will be completed.',
        indicatorType: 'info',
      };
    }

    if (selectedDocument.status === DocumentStatus.PENDING_REVIEW) {
      return {
        primaryDescription: `${signingAction.signerName} is reviewing this document`,
        secondaryDescription: "Once they're done, this document will be completed.",
        indicatorType: 'info',
      };
    }

    if (signingAction.type === SigningActionType.VIEW && selectedDocument.id === signingAction.document?.id) {
      if (signingAction.isNotary) {
        return {
          primaryDescription: `${signingAction.signerName} is notarizing this document`,
          secondaryDescription: "Once they're done, they will review this document.",
          indicatorType: 'info',
        };
      } else {
        return {
          primaryDescription: `${signingAction.signerName} is signing this document`,
          // TODO: this text is supposed to be conditional, but right now can't derive whether current
          // participant has already signed or not.
          // See Figma: https://www.figma.com/file/DRwKYYXObepLKa3IUds8Cf/Signing-Room?node-id=7016%3A35
          secondaryDescription: 'Once everyone has signed, this document will be completed.',
          indicatorType: 'info',
        };
      }
    }

    if (selectedDocument.status === DocumentStatus.COMPLETED) {
      return {
        primaryDescription: 'This document has been completed',
        secondaryDescription: 'It has been signed and notarized by all necessary participants.',
        indicatorType: 'affirm',
      };
    }

    if (selectedDocument.status === DocumentStatus.VOIDED) {
      return {
        primaryDescription: 'This document has been voided',
        secondaryDescription: 'It will still be available in the package, but no further action can be taken.',
        indicatorType: 'void',
      };
    }

    return null;
  })();

  if (!subheaderAttributes) {
    return null;
  }

  const { indicatorType, primaryDescription, secondaryDescription } = subheaderAttributes;

  return (
    <DocSubheaderWrapper data-it="Participant-DocSubheader">
      <div className={css(styles.wrapper, (styles as any)[indicatorType])}>
        <div className={css(styles.primaryDescription)}>{primaryDescription}</div>
        {secondaryDescription && <div className={css(styles.secondaryDescription)}>{secondaryDescription}</div>}
      </div>
    </DocSubheaderWrapper>
  );
};
